import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Form, Button, Popover, Popconfirm, message } from 'antd';
import { List, AutoSizer, ListRowProps } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import * as Yup from 'yup';
import DrawerHookForm from '../../global/drawer/DrawerHookForm';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import PackageDrawerField from './PackageDrawerField';
import CreatePackageDrawer from './CreatePackageDrawer';
import { updatePackage } from '../../../actions/items/package/update';
import { typingDone } from '../../../utils/Utils';
import { useAppDispatch } from '../../../store/ReduxStore';

type PackageDrawerProps = {
  uomId?: number;
  visible: boolean;
  onClose: () => void;
  title: string;
  uomName: string;
  showCreatePkg: boolean;
};

const PackageDrawer: React.FC<PackageDrawerProps> = props => {
  const DEFAULT_ROW_HEIGHT = 40;
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const listRef = React.useRef<List>(null);

  const [showCreatePkg, setShowCreatePkg] = React.useState<boolean>(props.showCreatePkg);

  const {
    allSelectedItemIds,
    selectedItemsList,
    reverseSelected,
    reversedItemIds,
    itemPackages,
    uoms,
  } = useSelector(
    (state: ApplicationState) => ({
      allSelectedItemIds: state.catalogue.catalogue.allSelectedItemIds,
      selectedItemsList: state.catalogue.catalogue.selectedItemsList,
      reverseSelected: state.catalogue.catalogue.reverseSelected,
      reversedItemIds: state.catalogue.catalogue.reversedItemIds,
      itemPackages: state.items.packageSeg.itemPackages,
      uoms: state.resources.data.package.uoms,
    }),
    shallowEqual
  );

  const [visibleCreatePkgDrawer, setVisibleCreatePkgDrawer] = React.useState<boolean>(false);

  React.useEffect(() => {
    setShowCreatePkg(props.showCreatePkg);
  }, [props.showCreatePkg]);

  const getSelectedItemIds = () =>
    reverseSelected
      ? allSelectedItemIds.filter(id => !reversedItemIds.includes(id))
      : allSelectedItemIds;

  const getSelectedItems = () =>
    selectedItemsList.filter(item => !reversedItemIds.includes(item.id));

  const handleSubmit = async (values: any) => {
    const itemIds = getSelectedItemIds();

    const updatedPackages = itemIds.map(itemId => {
      const itemPkg = itemPackages.find(iPkg => iPkg.item_id === itemId);
      const packages = itemPkg?.packages || [];

      // Get all changed packages for this item with the current UOM
      const changedPackagesForItem = values.itemValues.filter(
        (pkg: any) => pkg.itemId === itemId && pkg.uom_id
      );

      if (itemPkg && changedPackagesForItem.length > 0) {
        // Remove all existing packages with this UOM ID from the item
        const packagesWithoutCurrentUom = packages.filter(pkg => pkg.uom_id !== props.uomId);

        // Add all the updated packages for this UOM
        const updatedPackagesForUom = changedPackagesForItem.map((changedPkg: any) => {
          // Remove the helper properties we added
          const { itemId, packageIndex, uniqueKey, ...packageData } = changedPkg;
          return packageData;
        });

        return {
          ...itemPkg,
          packages: [...packagesWithoutCurrentUom, ...updatedPackagesForUom],
        };
      }

      if (itemPkg) {
        // No changes for this item, return as is
        return { ...itemPkg, packages: itemPkg.packages };
      }

      // New item with packages
      const newPackagesForItem = changedPackagesForItem.map((changedPkg: any) => {
        const { itemId, packageIndex, uniqueKey, ...packageData } = changedPkg;
        return packageData;
      });
      return { item_id: itemId, packages: newPackagesForItem };
    });

    try {
      await dispatch(updatePackage(updatedPackages));
      props.onClose();
    } catch (e: any) {
      message.error(e.message);
      throw e;
    }
  };

  const validationSchema = Yup.object().shape(
    {
      uom_id: Yup.number(),
      itemValues: Yup.array().of(
        Yup.object().shape({
          quantity_of_eaches: Yup.number()
            .nullable()
            .when('uom_id', {
              is: true,
              then: schema => schema.nullable().required(t('validation:required')),
              otherwise: schema => schema.notRequired(),
            }),
        })
      ),
    },
    [['quantity_of_eaches', 'uom_id']]
  );

  const getDefaultValues = () => {
    const itemIds = getSelectedItemIds();

    // Get all packages for each item with the matching UOM ID (not just the first one)
    const allItemPackages = itemIds.flatMap(itemId => {
      const item = itemPackages.find(itemPackage => itemPackage.item_id === itemId);
      const packages = item?.packages.filter(pkg => pkg.uom_id === props.uomId) || [];

      if (packages.length > 0) {
        // Return all packages for this item, each with a unique identifier
        return packages.map((pkg, index) => ({
          ...pkg,
          itemId,
          packageIndex: index, // Add index to distinguish multiple packages from same item
          uniqueKey: `${itemId}_${pkg.id}_${index}`, // Unique key for form handling
        }));
      }

      // If no packages found, return a placeholder with all required Package properties
      return [
        {
          itemId,
          packageIndex: 0,
          uniqueKey: `${itemId}_new_0`,
          id: `temp_${itemId}_0`,
          uom_id: null as any, // Will be set to the actual UOM ID when user creates package
          level_gtin: null,
          quantity_of_eaches: null,
          electronic_product_code: null,
          bar_code_characters: null,
          multi_box_number: null,
          inner_quantity: null,
          inner_quantity_uom_id: null,
          orderable_option_id: null,
          height: null,
          width: null,
          length: null,
          merchandising_height: null,
          merchandising_width: null,
          merchandising_length: null,
          dimensions_uom_id: null,
          weight: null,
          weights_uom_id: null,
          weight_variance: null,
          dimensional_weight: null,
          stacking_factor: null,
          unique_key: null,
          value_key: null,
          hazardous_materials: [],
          uomName: '',
          summary: '',
          temp: true,
          detailedSummary: [],
          dimensionsUomName: '',
          weightName: '',
          originalUomId: undefined,
        },
      ];
    });

    return { itemValues: allItemPackages };
  };

  return (
    <DrawerHookForm
      enableReinitialize
      title={props.title}
      visible={props.visible}
      onClose={() => props.onClose()}
      defaultValues={getDefaultValues()}
      width="60%"
      onSubmit={values => handleSubmit(values)}
      additionalFooterData={
        <Button
          data-testid="create-pkg"
          onClick={() => setVisibleCreatePkgDrawer(true)}
          disabled={!showCreatePkg}
        >
          {t('packageSeg:createPkg')}
        </Button>
      }
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {({ setValue, watch, formState }) => {
        typingDone(() => listRef.current?.recomputeRowHeights());

        const selectedItems = getSelectedItems();
        const values = watch();
        const { errors } = formState;

        const replaceAllValues = (
          inputValue: any,
          title: string,
          selectValue?: number,
          height?: string,
          length?: string
        ) => {
          let itemValues = values.itemValues;

          if (title.includes(t('packageSeg:package.quantityEach'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              quantity_of_eaches: Number(inputValue) || null,
            }));
          }
          if (title.includes(t('packageSeg:package.innerQuantity'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              inner_quantity: inputValue,
              inner_quantity_uom_id: selectValue || null,
            }));
          }
          if (title.includes(`${t('packageSeg:package.shippingDimensions')}`)) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              width: inputValue,
              dimensions_uom_id: selectValue || null,
              height,
              length,
            }));
          }
          if (title.includes(t('packageSeg:package.weight'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              weight: inputValue,
              weights_uom_id: selectValue || null,
            }));
          }
          if (title.includes(`${t('packageSeg:package.weightVariance')}`)) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              weight_variance: inputValue,
            }));
          }
          if (title.includes(`${t('packageSeg:package.merchandisingDimensions')}`)) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              merchandising_width: inputValue,
              dimensions_uom_id: selectValue || null,
              merchandising_height: height,
              merchandising_length: length,
            }));
          }
          if (title.includes(t('packageSeg:package.orderable'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              orderable_option_id: selectValue || null,
            }));
          }
          if (title.includes(t('packageSeg:package.stackingFactor'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              stacking_factor: inputValue,
            }));
          }
          if (title.includes(t('packageSeg:package.levelGtin'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              level_gtin: inputValue,
            }));
          }
          if (title.includes(t('packageSeg:package.electronicProductCode'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              electronic_product_code: inputValue,
            }));
          }
          if (title.includes(t('packageSeg:package.barcodeChar'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              bar_code_characters: inputValue,
            }));
          }
          if (title.includes(t('packageSeg:package.multiBoxNumber'))) {
            itemValues = values.itemValues.map((pkg: any) => ({
              ...pkg,
              multi_box_number: inputValue,
            }));
          }
          setValue('itemValues', itemValues, { shouldDirty: true, shouldValidate: true });
        };

        const rowRenderer = ({ key, index, style }: ListRowProps) => {
          const pkg = values.itemValues[index];

          // Find the corresponding item based on the itemId in the package
          const item = selectedItems.find(selectedItem => selectedItem.id === pkg.itemId);

          const valueErrors = errors.itemValues;
          const showValidationInfo = Array.isArray(valueErrors) && !!valueErrors[index];

          // Show package index if there are multiple packages for the same item
          const packageLabel = pkg.packageIndex > 0 ? ` (${pkg.packageIndex + 1})` : '';

          return (
            <div style={style} key={key} className="package__drawer-list-row">
              <div className="package__drawer-list-row_data">
                {item?.part_number}
                {packageLabel && <span className="text-gray-500 text-sm ml-1">{packageLabel}</span>}
              </div>

              <div className="package__drawer-list-row_data">
                {item?.short_name ? (
                  <Popover content={item.short_name}>
                    <span>{item.short_name}</span>
                  </Popover>
                ) : (
                  <span className="italic text-gray-600">{t('packageSeg:noPartType')}</span>
                )}
              </div>

              <Form.Item
                hasFeedback
                validateStatus={showValidationInfo ? 'error' : ''}
                help={(showValidationInfo && t('validation:required')) || undefined}
              >
                <PackageDrawerField
                  title={props.title}
                  uoms={uoms}
                  disabled={!pkg?.uom_id}
                  uomName={props.uomName}
                  values={pkg}
                  handleOnChange={(value, name) => {
                    setValue(`itemValues.${index}.${name}`, value || null, { shouldDirty: true });
                  }}
                />
              </Form.Item>
            </div>
          );
        };

        return (
          <React.Fragment>
            <Form
              layout="vertical"
              className="h-full flex flex-col package__drawer overflow-hidden"
            >
              <div className="package__drawer-list-row font-medium text-black bg-gray-200">
                <div className="package__drawer-list-row_data">{t('packageSeg:partNumber')}</div>

                <div className="package__drawer-list-row_data">
                  {t('packageSeg:productDescription')}
                </div>

                <div className="flex">
                  <div className="flex-1">{t('packageSeg:value')}</div>
                  <div className="text-right">
                    <Popconfirm
                      placement="left"
                      title={t('packageSeg:warnClearText')}
                      onConfirm={() => replaceAllValues('', props.title)}
                      okText={t('common:yes')}
                      cancelText={t('common:cancel')}
                      onCancel={e => e?.stopPropagation()}
                      disabled={showCreatePkg}
                    >
                      <Button size="small" disabled={showCreatePkg}>
                        {t('packageSeg:clearAll')}
                      </Button>
                    </Popconfirm>
                  </div>
                </div>
              </div>
              <div className="package__drawer-list-row">
                <div />
                <div className="package__drawer-list-row_text font-medium text-black">
                  {t('packageSeg:overwriteAllValues')}
                </div>
                <div className={classNames('package-drawer__overwrite-wrapper')}>
                  <PackageDrawerField
                    title={props.title}
                    uoms={uoms}
                    displayOk
                    uomId={props.uomId!}
                    disabled={showCreatePkg}
                    handleReplace={(inputValue, selectValue, height, length) =>
                      replaceAllValues(inputValue, props.title, selectValue, height, length)
                    }
                    uomName={props.uomName}
                    overWriteInfo
                  />
                </div>
              </div>

              <div className="flex-1">
                <AutoSizer>
                  {({ height, width }) => (
                    <List
                      ref={listRef}
                      width={width}
                      height={height}
                      rowCount={values.itemValues.length}
                      rowRenderer={rowRenderer}
                      rowHeight={({ index }) => {
                        const showValidationInfo =
                          Array.isArray(errors.itemValues) && !!errors.itemValues[index];
                        return showValidationInfo ? DEFAULT_ROW_HEIGHT + 20 : DEFAULT_ROW_HEIGHT;
                      }}
                    />
                  )}
                </AutoSizer>
              </div>
            </Form>
            <CreatePackageDrawer
              visible={visibleCreatePkgDrawer}
              uomId={props.uomId!}
              title={`${t('packageSeg:package.quantityEach')} „${props.uomName}”`}
              uoms={uoms}
              onClose={(sucessSubmit?: boolean) => {
                setVisibleCreatePkgDrawer(false);
                if (sucessSubmit) setShowCreatePkg(false);
              }}
            />
          </React.Fragment>
        );
      }}
    </DrawerHookForm>
  );
};

export default PackageDrawer;
